import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { NotificationAdapter } from 'src/backoffice-app-code/externalServices/adapters/NotificationServiceAdapter';
import { LogError } from 'src/backoffice-app-code/externalServices/implementations/ConsoleLoggerService';
import {
  FETCH_LOGIN,
  IS_LOGGED_IN,
} from 'src/backoffice-app-code/libs/reactQuery/configs/reactQueryCashKeys';
import { useDefaultRQConfig } from 'src/backoffice-app-code/libs/reactQuery/hooks/reactQueryBase';
import { LoginRoot } from 'src/backoffice-app-code/routes/Routes';
import { getServerErrorMessage } from 'src/backoffice-app-code/utils/errorUtils';
import { AuthRepository } from '../../infrastracture/repositories/AuthRepository';
import { UpdatePasswordFormData } from '../../models/UpdatePasswordFormData';
import { clearTokens } from '../localStorages/tokenLocalStorage';
import { useUser } from './useUser';

export interface IUseAuth {
  login: (params: { email: string; password: string }) => Promise<void>;
  loginIsLoading: boolean;
  loginErrorMessage: string | undefined;
  logout: () => void;
  changePasswordAsync: (data: UpdatePasswordFormData) => Promise<void>;
  isChangingPassword: boolean;
}

export function useAuth(): IUseAuth {
  const navigate = useNavigate();
  const { clearUser } = useUser();
  const config = useDefaultRQConfig('useAuth');
  const { EnqueueMessage } = NotificationAdapter();
  const queryClient = useQueryClient();

  const {
    mutateAsync: login,
    error: loginError,
    isLoading: loginIsLoading,
  } = useMutation(
    [FETCH_LOGIN],
    async (params: { email: string; password: string }) => {
      await AuthRepository.Login(params.email, params.password);
    },
    {
      ...config,
      onMutate: () => {},
      onSuccess: () => {
        EnqueueMessage('Ulogovali ste se!', 'success');
        clearUser();
        queryClient.setQueryData([IS_LOGGED_IN], true);
      },
    }
  );

  const { mutateAsync: changePasswordAsync, isLoading: isChangingPassword } = useMutation(
    async (data: UpdatePasswordFormData) => {
      await AuthRepository.ChangePassword(data);
    },
    {
      onSuccess: () => {
        EnqueueMessage('Password changed successfully', 'success');
        navigate(LoginRoot);
      },
      onError: (error: unknown) => {
        EnqueueMessage(getServerErrorMessage(error), 'error');
        LogError(error, 'Error while calling api', 'useUser->changePassword');
      },
    }
  );

  const logout = () => {
    clearTokens();
    clearUser();
    queryClient.setQueryData([IS_LOGGED_IN], false);
    EnqueueMessage('Izlogovali ste se!', 'success');
    navigate('/login');
  };

  return {
    login,
    loginIsLoading,
    loginErrorMessage: loginError ? getServerErrorMessage(loginError) : undefined,
    logout,
    changePasswordAsync,
    isChangingPassword,
  };
}
